import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Input, Container, Row } from "reactstrap";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { IBrand } from "../../../../../interfaces/company";
import userService from "../../../../../services/userService";
import Button from "../../../../../components/common/button";
import Loader from "../../../../../components/loader";
import CreateCard from "./createCard";
import BrandTable from "./brandTable";
import { IApplicationState } from "../../../../../redux/reducers";
import PermissionNotice from "../../../permissionNotice";

interface IProps extends RouteComponentProps {
	getBrands: () => Promise<any[]>;
	removeBrand: (id: string) => Promise<any>;
	addBrand: (name: string) => Promise<any>;
	editBrand: (id: string, name: string) => Promise<any>;
	role?: any;
	subscription_type?: string;
	isTrial?: boolean;
}

const Brand: React.FC<IProps> = ({ getBrands, addBrand, removeBrand, editBrand, role, subscription_type, isTrial, history }) => {
	const [brands, setBrands] = useState<IBrand[]>([]);

	const [isLoading, setLoading] = useState<boolean>(false);
	const [isRemoveLoading, setRemoveLoading] = useState<boolean>(false);
	const [isEditLoading, setEditLoading] = useState<boolean>(false);

	const [selectedBrand, setSelectedBrand] = useState<IBrand | null>(null);
	const [removeModal, setRemoveModal] = useState<boolean>(false);
	const [editModal, setEditModal] = useState<boolean>(false);

	const [editBrandText, setEditText] = useState<string>("");

	const isFreeTier = subscription_type === "free" || subscription_type === "free_trial" || !subscription_type;
	const brandLimit = 5;
	const isLimitReached = isFreeTier && brands.length >= brandLimit;

	useEffect(() => {
		setLoading(true);
		getBrands()
			.then(setBrands)
			.finally(() => {
				setLoading(false);
			});
	}, []);

	function deleteBrand() {
		setRemoveLoading(true);
		removeBrand(selectedBrand?.id || "")
			.then(() => {
				setBrands(brands.filter((item) => item.id !== selectedBrand?.id));
			})
			.finally(() => {
				setSelectedBrand(null);
				setRemoveLoading(false);
				setRemoveModal(false);
			});
	}

	function updateBrand() {
		setEditLoading(true);
		editBrand(selectedBrand?.id || "", editBrandText)
			.then((res: IBrand) => {
				if (res?.id && selectedBrand) {
					const brandIndex = brands.indexOf(selectedBrand);
					let updatedBrands = [...brands];
					updatedBrands[brandIndex] = res;
					setBrands(updatedBrands);
				}
			})
			.finally(() => {
				setSelectedBrand(null);
				setEditModal(false);
				setEditLoading(false);
			});
	}

	const handleBrandRemoveSelect = (brand: IBrand) => {
		setSelectedBrand(brand);
		setRemoveModal(true);
	};

	const handleBrandEditSelect = (brand: IBrand) => {
		setSelectedBrand(brand);
		setEditModal(true);
		setEditText(brand.name || "");
	};

	async function add(name: string) {
		if (name)
			return addBrand(name).then(() => {
				getBrands().then(setBrands);
			});
	}

	if (subscription_type === "supplier") {
		return (
			<div className="p-5 text-center">
				<h4 className="text-muted">Brand Management is not available on your plan.</h4>
				<p>Please upgrade your plan to access this feature.</p>
				<Button
					color="primary"
					onClick={() => history.push("/app/subscribe")}
				>
					Upgrade Now
				</Button>
			</div>
		);
	}

	return (
		<>
			{role !== "COMPANY_USER" ? (
				<div>
					<Container style={{ marginLeft: 0, maxWidth: "100%" }}>
						<Row style={{ flexWrap: "nowrap", justifyContent: "flex-start", alignItems: "center" }}>
							<CreateCard
								style={{ width: "100%", maxWidth: "800px" }}
								title={"Brand"}
								addBrand={add}
								disabled={isLimitReached}
								disabledMessage={isLimitReached ? "Brand limit reached" : undefined}
							/>
							{isLimitReached && (
								<div className="ml-4 d-flex align-items-center" style={{ gap: "10px" }}>
									<span style={{ color: "#e85a73", fontWeight: 600 }}>Limit Exceeded</span>
									<Button
										color="primary"
										size="sm"
										onClick={() => history.push("/app/subscribe")}
										style={{ padding: "6px 12px" }}
									>
										Upgrade
									</Button>
								</div>
							)}
						</Row>
					</Container>

					<Loader isLoading={isLoading}>
						<div className="company-brand">
							{brands && (
								<BrandTable
									brands={brands}
									handleBrandEditSelect={handleBrandEditSelect}
									handleBrandRemoveSelect={handleBrandRemoveSelect}
								/>
							)}
						</div>

						<Modal
							className="brand-modal"
							isOpen={!!selectedBrand && removeModal}
							size="md"
							toggle={() => setSelectedBrand(null)}
						>
							<ModalHeader>Remove Brand</ModalHeader>
							<ModalBody>
								<p>Are you sure you want to remove brand "{selectedBrand?.name}"?</p>
								<Button
									color="danger"
									onClick={deleteBrand}
									className="color-white"
									loading={isRemoveLoading}
									style={{ float: "right" }}
								>
									Remove Brand
								</Button>
								<Button
									color="secondary"
									className="mr-2"
									onClick={() => setSelectedBrand(null)}
									style={{ float: "right" }}
								>
									Cancel
								</Button>
							</ModalBody>
						</Modal>

						<Modal
							className="brand-modal"
							isOpen={!!selectedBrand && editModal}
							size="md"
							toggle={() => setEditModal(false)}
						>
							<ModalHeader>Edit Brand </ModalHeader>
							<ModalBody>
								<h5>Brand Name</h5>
								<Input
									placeholder="Brand Name"
									onChange={(e) => setEditText(e.target.value)}
									value={editBrandText}
									className={"mb-3"}
									style={{ width: "100%", height: 40 }}
								/>
								<Button
									color="primary"
									onClick={updateBrand}
									className="color-white"
									loading={isEditLoading}
									style={{ float: "right" }}
								>
									Edit Brand
								</Button>
								<Button
									color="secondary"
									className="mr-2"
									onClick={() => setEditModal(false)}
									style={{ float: "right" }}
								>
									Cancel
								</Button>
							</ModalBody>
						</Modal>
					</Loader>
				</div>
			) : (
				<PermissionNotice />
			)}
		</>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	role: state.user?.profile?.role,
	subscription_type: state.user?.profile?.company?.subscription_type,
	isTrial: state.user?.permissions?.is_trial
});

const mapDispatchToProps = {
	getBrands: () => userService.getBrands(),
	removeBrand: (id: string) => userService.removeBrand(id),
	addBrand: (name: string) => userService.addBrand(name),
	editBrand: (id: string, name: string) => userService.editBrand(id, name)
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Brand));
